import { isAuthCircuitOpen, openAuthCircuit } from '@/lib/auth-circuit';
import { isTerminalAuthRefreshError } from '@/lib/mobile/resume-pipeline';
import { supabase } from '@/integrations/supabase/client';

import type { QueryClient } from '@tanstack/react-query';

/**
 * Circuit breaker: prevents cascading errors when a session expires.
 * The first auth error cancels all in-flight queries and triggers local signout.
 * The circuit stays open until the user re-authenticates (SIGNED_IN event).
 *
 * `trigger` is a PII-free id naming which kill path fired - the one field the
 * next incident report needs and the last one had to be reconstructed
 * forensically. Never attach error messages here: GoTrue messages can echo
 * account context.
 */
export function handleAuthCircuitBreak(queryClient: QueryClient, trigger = 'unspecified'): void {
  if (isAuthCircuitOpen()) {
    return;
  }
  openAuthCircuit();
  console.error('[AuthCircuit] tripped', { trigger });
  // Cancel all in-flight queries immediately (fire-and-forget).
  queryClient.cancelQueries().catch(() => {
    /* intentional swallow */
  });
  clearAuthAfterCircuitBreak().catch(() => {
    /* intentional swallow */
  });
}

// One verification at a time: an auth-shaped error burst (every widget 401ing
// at once) must produce one refresh and one outcome, not a stampede.
let verificationInFlight: Promise<void> | null = null;

/**
 * Verify-before-kill: an auth-SHAPED error (any 401, or a matched message
 * pattern) is not proof the session is dead - Apple-device storage hiccups,
 * post-resume races and edge-function cold starts all produce them. One
 * `refreshSession()` decides: success heals the screen (failed queries
 * refetch, no toast); a terminal error (shared classifier - includes
 * `user_banned`, so suspension actually takes effect) breaks the circuit; a
 * transient error keeps the session and re-arms for the next auth error.
 */
export function verifyAuthThenBreak(queryClient: QueryClient, trigger: string): void {
  if (isAuthCircuitOpen()) {
    return;
  }
  verificationInFlight ??= (async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        if (isTerminalAuthRefreshError(error)) {
          handleAuthCircuitBreak(queryClient, trigger);
        }
        return;
      }
      if (data.session) {
        // The queries that errored are dead (auth errors return retry: false);
        // refetching active queries is what makes the recovery silent instead
        // of a screen of stale error states.
        await queryClient.invalidateQueries({ refetchType: 'active' });
      } else {
        // Success with no session is out-of-contract: the server answered, so
        // this is not a network blip, and treating it as one would loop
        // auth-shaped errors forever with the circuit never opening. Fail
        // closed - an unverifiable session on a constituent-data app signs
        // out rather than lingers.
        handleAuthCircuitBreak(queryClient, `${trigger}:no_session`);
      }
    } catch {
      // Transport failure: transient by definition - keep the session.
    } finally {
      verificationInFlight = null;
    }
  })();
}

export function __resetAuthVerificationForTests(): void {
  verificationInFlight = null;
}

export async function clearAuthAfterCircuitBreak(): Promise<void> {
  // Local-only signout: clears localStorage without API call
  // (the server-side session is already invalid)
  await supabase.auth.signOut({ scope: 'local' });
}
